import { Shield, Target, Zap, Award, Crown, Users, Trophy, Star, TrendingUp, Activity } from "lucide-react";
import { type TeamStats, type MatchSummary, type HomeAwayComparison, type TopPlayer } from "@/api/tournamentAnalysis";

export interface Achievement {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    unlocked: boolean;
    unlockedBy: string[];
    rarity: "common" | "rare" | "epic" | "legendary";
}


const calculateTeamMetrics = (topPlayers: TopPlayer[]) => {
    if (!topPlayers || topPlayers.length === 0) {
        return {
            totalKPIPoints: 0,
            averageKPIPerPlayer: 0,
            highPerformerCount: 0,
            playersWithMinKPI: 0,
            totalPlayers: 0,
            totalOffensivePoints: 0,
            totalDefensivePoints: 0,
            totalTeamPlayPoints: 0,
            totalEffortPoints: 0,
        };
    }

    const totalKPIPoints = topPlayers.reduce((sum, player) => sum + player.kpiScore, 0);
    const averageKPIPerPlayer = totalKPIPoints / topPlayers.length;
    const highPerformerCount = topPlayers.filter((player) => player.kpiScore >= 70).length;
    const playersWithMinKPI = topPlayers.filter((player) => player.kpiScore >= 50).length;

    const totalOffensivePoints = Math.round(totalKPIPoints * 0.28);
    const totalDefensivePoints = Math.round(totalKPIPoints * 0.25);
    const totalTeamPlayPoints = Math.round(totalKPIPoints * 0.3);
    const totalEffortPoints = Math.round(totalKPIPoints * 0.17);

    return {
        totalKPIPoints,
        averageKPIPerPlayer,
        highPerformerCount,
        playersWithMinKPI,
        totalPlayers: topPlayers.length,
        totalOffensivePoints,
        totalDefensivePoints,
        totalTeamPlayPoints,
        totalEffortPoints,
    };
};

export const generateTournamentAchievements = (
    teamStats: TeamStats,
    matchSummary: MatchSummary,
    homeAwayComparison: { home: HomeAwayComparison; away: HomeAwayComparison },
    topPlayers: TopPlayer[],
    teamName: string
): Achievement[] => {
    if (!teamStats || !matchSummary || !homeAwayComparison) return [];

    const createAchievement = (
        id: string,
        name: string,
        description: string,
        icon: React.ReactNode,
        unlocked: boolean,
        rarity: Achievement["rarity"],
        unlockedBy?: string[]
    ): Achievement => ({
        id,
        name,
        description,
        icon,
        unlocked,
        unlockedBy: unlockedBy || (unlocked ? [teamName] : []),
        rarity,
    });

    const hasPlayerData = topPlayers && topPlayers.length > 0;
    const teamData = hasPlayerData ? calculateTeamMetrics(topPlayers) : null;

    return [
        createAchievement(
            "winning_spirit",
            "Winning Spirit",
            "Win more games than you lose",
            <Trophy className="w-5 h-5" />,
            matchSummary.wins > matchSummary.losses,
            "common"
        ),

        createAchievement(
            "home_advantage",
            "Home Advantage",
            "Win rate at home is 60% or higher",
            <Users className="w-5 h-5" />,
            homeAwayComparison.home.winRate >= 60,
            "rare"
        ),

        createAchievement(
            "road_warriors",
            "Road Warriors",
            "Win rate away from home is 50% or higher",
            <Target className="w-5 h-5" />,
            homeAwayComparison.away.winRate >= 50,
            "rare"
        ),

        createAchievement(
            "sharpshooters",
            "Sharpshooters",
            "Team shot accuracy above 60%",
            <Zap className="w-5 h-5" />,
            teamStats.shotAccuracy >= 60,
            "epic"
        ),

        createAchievement(
            "defensive_fortress",
            "Defensive Fortress",
            "Team save percentage above 90%",
            <Shield className="w-5 h-5" />,
            teamStats.savePercentage >= 90,
            "epic"
        ),

        createAchievement(
            "possession_masters",
            "Possession Masters",
            "Corsi percentage above 50% (controlling the puck)",
            <Activity className="w-5 h-5" />,
            teamStats.corsiPercentage >= 50,
            "rare"
        ),

        ...(hasPlayerData && teamData
            ? [
                  createAchievement(
                      "high_performance_squad",
                      "High Performance Squad",
                      "Have 3+ players earn the 'High Performer' badge (70+ KPI)",
                      <Star className="w-5 h-5" />,
                      teamData.highPerformerCount >= 3,
                      "legendary"
                  ),

                  createAchievement(
                      "team_average_excellence",
                      "Team Average Excellence",
                      "Team average 60+ KPI points per player",
                      <TrendingUp className="w-5 h-5" />,
                      teamData.averageKPIPerPlayer >= 60,
                      "epic"
                  ),

                  createAchievement(
                      "cohesive_unit",
                      "Cohesive Unit",
                      "Score 300+ total team KPI points",
                      <Award className="w-5 h-5" />,
                      teamData.totalKPIPoints >= 300,
                      "common"
                  ),

                  createAchievement(
                      "elite_performance",
                      "Elite Performance",
                      "Score 400+ total team KPI points",
                      <Crown className="w-5 h-5" />,
                      teamData.totalKPIPoints >= 400,
                      "legendary"
                  ),

                  createAchievement(
                      "no_player_left_behind",
                      "No Player Left Behind",
                      "Every player scores 50+ individual KPI points",
                      <Users className="w-5 h-5" />,
                      teamData.totalPlayers > 0 && teamData.playersWithMinKPI === teamData.totalPlayers,
                      "rare"
                  ),

                  createAchievement(
                      "balanced_excellence",
                      "Balanced Excellence",
                      "Strong performance in multiple areas (estimated)",
                      <Target className="w-5 h-5" />,
                      teamData.totalKPIPoints >= 350 && teamData.averageKPIPerPlayer >= 55,
                      "epic"
                  ),
              ]
            : []),
    ];
};
